import { useMemo } from "react";
import { trpc } from "../../lib/trpc.js";
import {
  estadoDaConsulta,
  resumoDoEstado,
  type EstadoDaFigurinha,
  type FigurinhasDasCamadas,
} from "./figurinhas-das-camadas.js";

/**
 * A validade da consulta. As URLs da biblioteca são assinadas e valem uma hora: refazer a consulta a cada meia
 * hora mantém as imagens da prévia carregando numa sessão longa, sem a tela de um operador que passa a tarde
 * imprimindo mostrar imagens quebradas.
 */
const UMA_MEIA_HORA = 30 * 60_000;

/**
 * As figurinhas que as camadas citam, uma consulta por id (`figurinhas.get`) — e não a lista da biblioteca:
 * a lista tem um teto, e a figurinha de uma camada pode não estar nela. Consultar cada uma é também o que diz,
 * com precisão, que ela **não existe mais** (apagada ou deixou de ser pública): o servidor responde
 * "não encontrada" para as duas, e é essa a resposta que o pedido da folha receberia.
 *
 * Ao escolher uma figurinha no seletor, a tela já grava a resposta no cache (`setData`), e a camada nasce
 * sem esperar a rede.
 */
export function useFigurinhasDasCamadas(ids: readonly number[]): FigurinhasDasCamadas {
  const consultas = trpc.useQueries((t) =>
    ids.map((id) =>
      t.figurinhas.get(
        { id },
        {
          staleTime: UMA_MEIA_HORA,
          refetchInterval: UMA_MEIA_HORA,
          // "Não encontrada" não passa a existir ao tentar de novo; uma queda de rede, sim.
          retry: (tentativas, erro) => erro.data?.code !== "NOT_FOUND" && tentativas < 2,
        },
      ),
    ),
  );

  // As consultas mudam de referência a cada render; o que importa é o que elas dizem.
  const chave = consultas.map(estadoDaConsulta).map(resumoDoEstado).join("|");

  return useMemo(() => {
    const mapa = new Map<number, EstadoDaFigurinha>();
    ids.forEach((id, i) => {
      const c = consultas[i];
      if (c) mapa.set(id, estadoDaConsulta(c));
    });
    return mapa;
    // `chave` resume `consultas`; `ids` é a lista, que o chamador mantém estável quando nada muda.
  }, [chave, ids.join(",")]);
}
